import React, { memo, useState } from "react";

import { formatNumber, useGetFarms, useTranslation } from "utils";

import { FieldListField, FieldShape } from "models";

import { ErrorBoundary, ExtraInfo, Flex, Icon, Link, MapStatic, Tag, Tooltip, Typography } from "components";

import { getFieldBaselineYearRange } from "pages/app/field-timeline/utils/PracticeUtils";

export type FieldHeadlineProps = {
  headline: string;
  field: FieldListField | null;
  shape: FieldShape | undefined;
  isLoading: boolean;
  isEnrolled: boolean | undefined;
  isBaseline?: boolean;
  onDelete?: () => void;
};

export const FieldHeadline = memo(({ headline, field, shape, isLoading, isEnrolled, isBaseline, onDelete }: FieldHeadlineProps) => {
  const { t } = useTranslation(["allFieldView", "allFieldsDataTableView"]);
  const { currentFarm } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram;
  const [openProgramInfoDrawer, setOpenProgramInfoDrawer] = useState<boolean>(false);
  const baselineYearRange = isBaseline ? getFieldBaselineYearRange(field?.baselineYears ?? []) : null;

  return (
    <div className="field-headline-card" data-testid="field-headline-card">
      {headline && (
        <Typography variant="h1" className="margin-bottom-150">
          {headline}
        </Typography>
      )}
      <Flex justify="space-between" align="center" gap="1rem">
        <div className="field-headline-map-container">
          {!isLoading && (
            <ErrorBoundary>
              {/* @ts-expect-error need to convert to tsx */}
              <MapStatic field={field} geometry={shape} location={field?.location} />
            </ErrorBoundary>
          )}
        </div>
        <Flex flex="2" vertical={true} align="flex-start">
          <Typography variant="h5" className="margin-0">
            {field?.name}
          </Typography>
          <Typography variant="paragraph-s" data-testid="field-headline-card-field-size" color="grey-600" className="margin-0">
            {formatNumber({ number: field?.size ?? 0 })}
            {t("fieldHeadlineCard.ha")}
          </Typography>
          {!isLoading && isBaseline && (
            <Tooltip variant="neutral" title={baselineYearRange ? t("allFieldsDataTableView:table.data.baselineTooltip", baselineYearRange) : undefined}>
              <Tag
                dataTestId="field-headline-card-field-baseline"
                className="margin-top-50 baseline-chip chip-pill capitalize"
                colour="blue"
                text={t("allFieldsDataTableView:table.header.baseline")}
              />
            </Tooltip>
          )}
        </Flex>
        <Flex align="center" gap="1rem">
          <Flex vertical align="flex-end" gap="0.25rem">
            {!isLoading && isEnrolled && (
              <Tag
                dataTestId="field-headline-card-field-enrollment-state"
                className="margin-right-0 chip-pill capitalize"
                colour="green-200"
                text={t("fieldHeadlineCard.enrolled")}
              />
            )}
            {!isLoading && isEnrolled && (
              <Link
                dataTestId="field-headline-card-field-carbon-program-learn-more"
                size="small"
                type="primary-link"
                onClick={() => setOpenProgramInfoDrawer((prevState) => !prevState)}
              >
                {t("fieldHeadlineCard.learnMore")}
              </Link>
            )}
          </Flex>
          {onDelete && <Icon className="pointer" dataTestId="field-headline-card-delete-crop" iconName="tabler/trash" onClick={onDelete} />}
        </Flex>
      </Flex>
      {carbonProgram && <ExtraInfo visible={openProgramInfoDrawer} type={carbonProgram.programSlug} setVisible={setOpenProgramInfoDrawer} />}
    </div>
  );
});
